import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Alert
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'qrcode';
import { TotpMultiFactorGenerator, multiFactor, signOut, sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase'; 
import { toast } from 'react-toastify';

const TwoFactorAuthCard = ({ user, onStatusChange }) => {
    const [mfaModalOpen, setMfaModalOpen] = useState(false);
    const [mfaQrCode, setMfaQrCode] = useState('');
    const [mfaVerificationCode, setMfaVerificationCode] = useState('');
    const [mfaSecret, setMfaSecret] = useState(null);
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaError, setMfaError] = useState('');
    
    // State to force re-render after enrollment/unenrollment
    const [mfaCheckTrigger, setMfaCheckTrigger] = useState(0);
    
    // State to track if we are waiting for the user to verify email
    const [isWaitingForEmailVerification, setIsWaitingForEmailVerification] = useState(false);

    const is2FAEnabled = useMemo(() => {
        const currentUser = auth.currentUser;
        if (!currentUser) return false;
        try {
            const mfaUser = multiFactor(currentUser);
            return (mfaUser?.enrolledFactors?.length ?? 0) > 0;
        } catch (err) {
            console.warn("2FA status check: multiFactor not available on currentUser", err);
            return false;
        }
    }, [user, mfaCheckTrigger]);

    // Helper to generate secret and show QR code
    const generateAndShowQRCode = async (currentUser) => {
        try {
            const mfaUser = multiFactor(currentUser);
            const session = await mfaUser.getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            const otpAuthUrl = secret.generateQrCodeUrl(currentUser.email || '', 'SavvaiEdge');
            const qrCodeDataUrl = await QRCode.toDataURL(otpAuthUrl);

            setMfaSecret(secret);
            setMfaQrCode(qrCodeDataUrl);
            setMfaModalOpen(true);
         } catch (error) {
        console.error("Error generating 2FA secret:", error);
        if (error.code === 'auth/unverified-email') {
            setIsWaitingForEmailVerification(true);
            setMfaError("Email not verified.");
            setMfaModalOpen(true);
        } else if (error.code === 'auth/requires-recent-login') {
            setMfaError("For security, please log out and log back in to enable 2FA.");
            toast.warning("For security, please log out and log back in to enable 2FA.");
        } else {
            setMfaError("Could not generate 2FA secret. Please try again.");
            toast.error("Could not generate 2FA secret. Please try again.");
        }
    }
    };

    const handleOpenEnable2FAModal = async () => {
    let currentUser = auth.currentUser;
    if (!currentUser) {
        toast.error("User not authenticated. Please refresh and try again.");
        return;
    }
    
    setMfaError('');
    setIsWaitingForEmailVerification(false);
    setMfaLoading(true);
    
    try {
        // Force reload the user to get fresh auth state (fixes first-time 2FA setup)
        await currentUser.reload();
        // Force refresh the ID token to ensure MFA session is valid
        await currentUser.getIdToken(true);
        // Get fresh reference after reload
        currentUser = auth.currentUser;
        
        await generateAndShowQRCode(currentUser);
    } catch (error) {
        console.error("Error preparing for 2FA setup:", error);
        if (error.code === 'auth/requires-recent-login') {
            toast.warning("For security, please log out and log back in to enable 2FA.");
            setMfaError("For security, please log out and log back in to enable 2FA.");
        } else {
            toast.error("Could not initialize 2FA setup. Please try again.");
        }
    }
    
    setMfaLoading(false);
};

    const handleSendVerificationEmail = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        
        try {
            setMfaLoading(true);
            
            const actionCodeSettings = {
                url: `${window.location.origin}/verify-email`,
                handleCodeInApp: true,
            };

            await sendEmailVerification(currentUser, actionCodeSettings);
            
            toast.success(`Verification email sent to ${currentUser.email}.`);
            // Stay in "Waiting" mode
        } catch (error) {
            console.error("Error sending verification email:", error);
            toast.error("Failed to send verification email.");
        } finally {
            setMfaLoading(false);
        }
    };

    const handleCheckVerificationStatus = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        setMfaLoading(true);
        try {
            // Force refresh the user token to get new emailVerified status
            await currentUser.reload();
            
            if (currentUser.emailVerified) {
                toast.success("Email verified successfully! Generating QR code...");
                setIsWaitingForEmailVerification(false);
                setMfaError('');
                // NOW generate the QR code for the first time
                await generateAndShowQRCode(currentUser);
            } else {
                toast.warning("Email still not verified. Please check your inbox and click the link.");
            }
        } catch (error) {
            console.error("Error checking verification:", error);
            toast.error("Could not verify status. Please try again.");
        } finally {
            setMfaLoading(false);
        }
    };

    const handleVerifyAndEnroll2FA = async () => {
        if (!mfaSecret || !mfaVerificationCode) {
            setMfaError("Verification code is required.");
            return;
        }
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setMfaError("Authentication error. Please log out and back in.");
            return;
        }
        
        let mfaUser;
        try {
             mfaUser = multiFactor(currentUser);
        } catch(err) {
             setMfaError("2FA not available. Re-login required.");
             return;
        }

        setMfaLoading(true);
        setMfaError('');

        try {
            const mfaAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                mfaSecret,
                mfaVerificationCode
            );
            await mfaUser.enroll(mfaAssertion, 'SavvaiEdge TOTP');
            toast.success("Two-Factor Authentication has been enabled!");
            
            setMfaCheckTrigger(prev => prev + 1);  // Force UI refresh
            if (onStatusChange) onStatusChange(); 
            
            setMfaModalOpen(false);
            setMfaVerificationCode('');
            setMfaQrCode('');
            setMfaSecret(null);
        } catch (error) {
            console.error("Error verifying 2FA code:", error);
            if (error.code === 'auth/invalid-verification-code') {
                setMfaError("Invalid code. Make sure you scanned the NEW QR code shown above.");
            } else {
                setMfaError("Failed to enable 2FA. Please try again.");
            }
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDisable2FA = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser || !is2FAEnabled) {
            toast.error("No 2FA enrollment found to disable.");
            return;
        }
        
        let mfaUser;
        try {
            mfaUser = multiFactor(currentUser);
        } catch (err) {
             toast.error("2FA status error. Please re-login.");
             return;
        }

        if (!window.confirm("Are you sure you want to disable Two-Factor Authentication? This will reduce your account security.")) {
            return;
        }

        const toastId = toast.loading("Disabling 2FA...");
        try {
            const tokenResult = await currentUser.getIdTokenResult();
            const authTime = new Date(tokenResult.claims.auth_time * 1000);
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

            if (authTime < fiveMinutesAgo) {
                toast.update(toastId, {
                    render: "For security, please log in again to disable 2FA.",
                    type: "warning",
                    isLoading: false,
                    autoClose: 5000
                });
                await signOut(auth);
                return;
            }

            const mfaInfo = mfaUser.enrolledFactors[0];
            await mfaUser.unenroll(mfaInfo.uid);

            setMfaCheckTrigger(prev => prev + 1);  // Force UI refresh
            if (onStatusChange) onStatusChange(); 
            
            toast.update(toastId, {
                render: "Two-Factor Authentication disabled.",
                type: "success",
                isLoading: false,
                autoClose: 3000
            });
        } catch (error) {
            console.error("Error disabling 2FA:", error);
            if (error.code === 'auth/requires-recent-login') {
                toast.update(toastId, {
                    render: "Please log in again to disable 2FA.",
                    type: "warning",
                    isLoading: false,
                    autoClose: 5000
                });
                await signOut(auth);
            } else {
                toast.update(toastId, {
                    render: `Failed to disable 2FA: ${error.message}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000
                });
            }
        }
    };

    const handleCancel2FASetup = () => {
        setMfaModalOpen(false);
        setMfaError('');
        setIsWaitingForEmailVerification(false);
        setMfaQrCode('');
        setMfaVerificationCode('');
        setMfaSecret(null);
    };

    return (
        <Box>
            <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                    Two-Factor Authentication (2FA)
                </Typography>
                <Divider sx={{ my: 2 }} />
                {is2FAEnabled ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <SecurityIcon color="success" sx={{ fontSize: 40 }} />
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                    2FA is Enabled
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Your account is protected with an authenticator app.
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDisable2FA}
                        >
                            Disable 2FA
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <SecurityIcon color="disabled" sx={{ fontSize: 40 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                        2FA is Disabled
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Increase your account security by enabling 2FA.
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                onClick={handleOpenEnable2FAModal}
                                disabled={mfaLoading}
                                sx={{ position: 'relative' }}
                            >
                                Enable 2FA
                                {mfaLoading && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Modal Dialog */}
            <Dialog open={mfaModalOpen} onClose={handleCancel2FASetup} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {isWaitingForEmailVerification ? "Verify Your Email" : "Enable Two-Factor Authentication"}
                        <IconButton onClick={handleCancel2FASetup}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                        
                        {/* Workflow A: Email Verification Needed */}
                        {isWaitingForEmailVerification ? (
                            <>
                                <Alert severity="warning" sx={{ width: '100%' }}>
                                    For security, you must verify your email address before enabling 2FA.
                                </Alert>
                                <Typography variant="body1" align="center">
                                    1. Click below to send a verification email.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    onClick={handleSendVerificationEmail}
                                    disabled={mfaLoading}
                                >
                                    Send Verification Email
                                </Button>
                                <Typography variant="body1" align="center">
                                    2. Click the link in the email (opens in new tab).
                                </Typography>
                                <Typography variant="body1" align="center">
                                    3. Come back here and click "I Have Verified".
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    onClick={handleCheckVerificationStatus}
                                    disabled={mfaLoading}
                                >
                                    I Have Verified My Email
                                </Button>
                            </>
                        ) : (
                            /* Workflow B: Setup 2FA */
                            <>
                                <Typography variant="body1" align="center">
                                    1. Scan this QR code with your authenticator app (Google Authenticator, 1Password).
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        display: 'inline-block'
                                    }}
                                >
                                    {mfaQrCode ? (
                                        <img
                                            src={mfaQrCode}
                                            alt="2FA QR Code"
                                            width="200"
                                            height="200"
                                        />
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </Box>
                                <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                                    2. Enter the 6-digit code from your app to verify.
                                </Typography>
                                <TextField
                                    label="Verification Code"
                                    variant="outlined"
                                    fullWidth
                                    value={mfaVerificationCode}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        setMfaVerificationCode(value);
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && mfaVerificationCode.length === 6 && !mfaLoading) {
                                            handleVerifyAndEnroll2FA();
                                        }
                                    }}
                                    disabled={mfaLoading}
                                    error={!!mfaError}
                                    helperText={mfaError}
                                    inputProps={{
                                        maxLength: 6,
                                        autoComplete: 'one-time-code',
                                        style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2rem' }
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCancel2FASetup} color="inherit" disabled={mfaLoading}>
                        Cancel
                    </Button>
                    {!isWaitingForEmailVerification && (
                        <Button
                            onClick={handleVerifyAndEnroll2FA}
                            variant="contained"
                            disabled={mfaLoading || mfaVerificationCode.length < 6}
                            sx={{ position: 'relative' }}
                        >
                            Verify & Enable
                            {mfaLoading && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TwoFactorAuthCard;